/**
 * StatsTab - World-Class Minimalist Design
 */
import React from 'react';
import { motion } from 'framer-motion';
import { Users, Building2, Briefcase, FileText, TrendingUp, BarChart3 } from 'lucide-react';

const translations = {
  en: {
    statistics: 'Statistics',
    subtitle: 'Overview of your organization',
    users: 'Users',
    total: 'Total',
    cases: 'Cases',
    teams: 'Teams',
    departments: 'Departments',
    documents: 'Documents',
    byRole: 'By Role',
    byStatus: 'By Status',
    loading: 'Loading...'
  },
  no: {
    statistics: 'Statistikk',
    subtitle: 'Oversikt over organisasjonen',
    users: 'Brukere',
    total: 'Totalt',
    cases: 'Saker',
    teams: 'Team',
    departments: 'Avdelinger',
    documents: 'Dokumenter',
    byRole: 'Per rolle',
    byStatus: 'Per status',
    loading: 'Laster...'
  }
};

interface StatsTabProps {
  stats: any;
  language?: string;
}

const StatsTab: React.FC<StatsTabProps> = ({ stats, language = 'no' }) => {
  const t = translations[language] || translations.no;

  if (!stats) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 bg-slate-100 rounded-xl animate-pulse" />
          <span className="text-sm text-slate-400">{t.loading}</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-slate-900">{t.statistics}</h2>
        <p className="text-slate-500 text-sm mt-1">{t.subtitle}</p>
      </div>

      <div className="space-y-6">
        {/* Quick Stats */}
        <div className="grid grid-cols-4 gap-4">
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-2xl p-5 border border-blue-100"
          >
            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm mb-3">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <p className="text-2xl font-semibold text-slate-900">{stats.users?.total || 0}</p>
            <p className="text-sm text-blue-600 mt-1">{t.users}</p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 rounded-2xl p-5 border border-emerald-100"
          >
            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm mb-3">
              <FileText className="w-5 h-5 text-emerald-600" />
            </div>
            <p className="text-2xl font-semibold text-slate-900">{stats.cases?.total || 0}</p>
            <p className="text-sm text-emerald-600 mt-1">{t.cases}</p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-br from-violet-50 to-violet-100/50 rounded-2xl p-5 border border-violet-100"
          >
            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm mb-3">
              <Building2 className="w-5 h-5 text-violet-600" />
            </div>
            <p className="text-2xl font-semibold text-slate-900">{stats.teams || 0}</p>
            <p className="text-sm text-violet-600 mt-1">{t.teams}</p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-gradient-to-br from-amber-50 to-amber-100/50 rounded-2xl p-5 border border-amber-100"
          >
            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm mb-3">
              <Briefcase className="w-5 h-5 text-amber-600" />
            </div>
            <p className="text-2xl font-semibold text-slate-900">{stats.departments || 0}</p>
            <p className="text-sm text-amber-600 mt-1">{t.departments}</p>
          </motion.div>
        </div>

        {/* Users by Role */}
        {stats.users?.by_role && Object.keys(stats.users.by_role).length > 0 && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-slate-50/50 rounded-2xl p-5"
          >
            <h3 className="font-medium text-slate-900 mb-4 flex items-center gap-2">
              <Users className="w-4 h-4 text-slate-400" />
              {t.users} - {t.byRole}
            </h3>
            <div className="grid grid-cols-4 gap-3">
              {Object.entries(stats.users.by_role).map(([role, count], index) => (
                <motion.div 
                  key={role}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.25 + index * 0.03 }}
                  className="bg-white rounded-xl p-4 text-center"
                >
                  <p className="text-2xl font-semibold text-slate-900">{count as number}</p>
                  <p className="text-xs text-slate-500 mt-1">{role}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Cases by Status */}
        {stats.cases?.by_status && Object.keys(stats.cases.by_status).length > 0 && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-slate-50/50 rounded-2xl p-5"
          >
            <h3 className="font-medium text-slate-900 mb-4 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-slate-400" />
              {t.cases} - {t.byStatus}
            </h3>
            <div className="grid grid-cols-4 gap-3">
              {Object.entries(stats.cases.by_status).map(([status, count], index) => (
                <motion.div 
                  key={status}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.35 + index * 0.03 }}
                  className="bg-white rounded-xl p-4 text-center"
                >
                  <p className="text-2xl font-semibold text-slate-900">{count as number}</p>
                  <p className="text-xs text-slate-500 mt-1 truncate">{status}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default StatsTab;
